import type { VariantProps } from 'class-variance-authority';
import { useQuery } from '@tanstack/react-query';
import {
  Badge,
  badgeVariants,
  Button,
  Paragraph,
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/atoms';
import { KeyValuePair } from '@/components/molecules/key-value-pair';
import { ArrowLeftIcon } from 'lucide-react';
import type { TransactionUI } from '../types';
import { fetchTransactionById } from '@/api/queries/transactions/request';
import { transformTransaction } from '../utils/transformTransaction';

const transactionStatusMap: Record<
  TransactionUI['status'],
  VariantProps<typeof badgeVariants>['variant']
> = {
  success: 'success',
  pending: 'warning',
  failed: 'destructive',
};

interface TransactionDetailModalProps {
  transaction: TransactionUI | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onClose: () => void;
}

export const TransactionDetailModal = ({
  transaction,
  open,
  onOpenChange,
  onClose,
}: TransactionDetailModalProps) => {
  const transactionId = transaction?.id;

  const { data } = useQuery({
    queryKey: ['transaction', transactionId],
    queryFn: () => fetchTransactionById(transactionId as string),
    enabled: !!transactionId && open,
  });

  if (!transaction) return null;

  const tx = data ? transformTransaction(data) : transaction;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-lg">
        <SheetHeader>
          <div className="flex items-center justify-between">
            <Button variant="ghost" size="icon" onClick={onClose}>
              <ArrowLeftIcon className="w-4 h-4" />
            </Button>
          </div>
          <SheetTitle className="text-xl lg:px-4 font-bold mt-4">
            Transaction Details
          </SheetTitle>
        </SheetHeader>

        <div className="px-8 space-y-4">
          {/* Transaction Info */}
          <div className="space-y-2">
            <Paragraph className="text-sm text-gray-600">
              Ref ID: {tx.refId}
            </Paragraph>
            <Paragraph className="text-sm flex items-center gap-2">
              Status:{' '}
              <Badge variant={transactionStatusMap[tx.status]}>
                {tx.statusLabel}
              </Badge>
            </Paragraph>
          </div>

          {/* Divider */}
          <div className="border-t border-gray-300 my-8"></div>

          {/* Transaction Details Grid */}
          <div className="grid grid-cols-2 gap-4">
            {/* Left Column */}
            <div className="space-y-6">
              <KeyValuePair label="Payment Method" value={tx.paymentMethod} />
              <KeyValuePair
                label="Account Number"
                value={tx.bankAccountNumber || '—'}
              />
              <KeyValuePair
                label="Account Name"
                value={tx.accountName || '—'}
              />
              <KeyValuePair label="Bank Name" value={tx.bankName || '—'} />
              <KeyValuePair label="Date/time" value={tx.fullDate} />
            </div>

            {/* Right Column */}
            <div className="space-y-6">
              <KeyValuePair label="Amount" value={tx.amount} />
              <KeyValuePair
                label="Transaction Type"
                value={tx.transactionType}
              />
              <KeyValuePair
                label="Platform Charge Amount (2%)"
                value={tx.platformChargeAmount || '—'}
              />
              <KeyValuePair
                label="Net Payout Amount"
                value={tx.netPayoutAmount || '—'}
              />
              <KeyValuePair
                label="Payment Status"
                value={
                  <Badge variant={transactionStatusMap[tx.status]}>
                    {tx.statusLabel}
                  </Badge>
                }
              />
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};
